import { motion } from "framer-motion";
import { Play, Anchor } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { scrollToId } from "@/lib/config";

const TEASERS = [
  {
    id: "about", title: "ABOUT THE SHOW", sub: "Real Mariners competing against each other.", badge: "EPISODE INTRO",
    image: "/assets/card-about.png", contain: true,
  },
  {
    id: "takes", title: "WHAT IT TAKES", sub: ["Maritime Skills", "Competition"], badge: "CHALLENGE BREAKDOWN",
    image: "/assets/card-takes.png",
  },
  {
    id: "mates", title: "CAPTAINS & FIRST MATES", sub: "First Adventure – Catalina Island", badge: "FIRST SERIES",
    image: "/assets/card-mates.png", coords: "Avalon Harbor · 33°20'46\"N, 118°19'32\"W",
  },
];

const subText = (sub) => (Array.isArray(sub) ? sub.join(" · ") : sub);

export const VideoTeasers = ({ onWatch }) => (
  <section id="teasers" className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="teasers-section">
    <div className="flex items-end justify-between mb-12">
      <div>
        <p className="text-[11px] tracking-[0.5em] uppercase text-[#D4AF37]">From The Helm</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-[0.08em] gold-text mt-3" data-testid="teasers-heading">
          Watch the Adventure unfold
        </h2>
      </div>
      <p className="hidden md:block font-coords text-[10px] tracking-[0.25em] text-[#94A3B8]" data-testid="teasers-coords">
        AVALON HARBOR · 33°20'46"N · 118°19'32"W
      </p>
    </div>

    <div className="grid md:grid-cols-3 gap-6 md:gap-8">
      {TEASERS.map((t, i) => (
        <motion.button
          key={t.id}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
          onClick={() => onWatch({ title: t.title, tagline: subText(t.sub), image: t.image })}
          className="group text-left gold-frame-corners"
          data-testid={`teaser-card-${t.id}`}
        >
          <div className="relative overflow-hidden border border-[#D4AF37]/25 bg-[#070A0F]">
            <img src={t.image} alt={t.title}
              className={`h-64 md:h-72 w-full transition-transform duration-700 ${t.contain ? "object-contain" : "object-cover group-hover:scale-105"}`} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070A0F]/80 via-transparent to-transparent" />
            <span className="absolute top-3 left-3 font-coords text-[9px] tracking-[0.25em] text-[#E8C15A] bg-[#070A0F]/70 border border-[#D4AF37]/30 px-2 py-1">
              {t.badge}
            </span>
            <span className="absolute inset-0 m-auto h-14 w-14 rounded-full border-2 border-[#D4AF37] text-[#D4AF37] flex items-center justify-center bg-[#070A0F]/40 backdrop-blur-sm group-hover:bg-[#D4AF37] group-hover:text-[#070A0F] transition-colors duration-300"
              data-testid={`teaser-play-${t.id}`}>
              <Play size={20} fill="currentColor" className="ml-0.5" />
            </span>
          </div>
          <h3 className="font-display text-base md:text-lg tracking-[0.12em] text-[#F5F3EF] mt-5 group-hover:text-[#E8C15A] transition-colors duration-300">
            {t.title}
          </h3>
          {Array.isArray(t.sub) ? (
            t.sub.map((line) => (
              <p key={line} className="font-editorial italic text-base text-[#94A3B8] mt-1 leading-tight">{line}</p>
            ))
          ) : (
            <p className="font-editorial italic text-base text-[#94A3B8] mt-1">{t.sub}</p>
          )}
          {t.coords && (
            <p className="font-coords text-[10px] tracking-[0.2em] text-[#E8C15A]/80 mt-2" data-testid={`teaser-coords-${t.id}`}>
              {t.coords}
            </p>
          )}
        </motion.button>
      ))}
    </div>
  </section>
);

export const TrailerModal = ({ trailer, onClose }) => (
  <Dialog open={!!trailer} onOpenChange={(open) => !open && onClose()}>
    <DialogContent aria-describedby={undefined} className="bg-[#0B111B] border border-[#D4AF37]/30 text-[#F7F8F9] max-w-2xl p-0 overflow-hidden" data-testid="trailer-modal">
      {trailer && (
        <div>
          <div className="relative">
            <img src={trailer.image} alt={trailer.title} className="w-full h-64 md:h-80 object-cover" />
            <div className="absolute inset-0 bg-[#070A0F]/55 flex items-center justify-center">
              <span className="h-16 w-16 rounded-full bg-[#D4AF37] text-[#070A0F] flex items-center justify-center shadow-[0_0_40px_rgba(212,175,55,0.5)]">
                <Play size={26} fill="currentColor" className="ml-1" />
              </span>
            </div>
          </div>
          <div className="p-6 md:p-8 text-center">
            <p className="font-coords text-[10px] tracking-[0.3em] text-[#E8C15A]">IN THE EDIT BAY</p>
            <h3 className="font-display text-xl md:text-2xl tracking-[0.1em] mt-3" data-testid="trailer-modal-title">{trailer.title}</h3>
            <p className="font-editorial italic text-lg text-[#94A3B8] mt-2">{trailer.tagline}</p>
            <p className="text-sm text-[#94A3B8] mt-4">
              The full cut premieres with the pilot episode. Join the crew and your vote decides who sails.
            </p>
            <button
              onClick={() => { onClose(); scrollToId("#community"); }}
              className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] text-[#070A0F] font-display text-xs tracking-[0.25em] px-6 py-3 hover:bg-[#F3E08A] transition-colors duration-300"
              data-testid="trailer-join-crew-btn"
            >
              <Anchor size={14} /> JOIN THE CREW
            </button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
